import React, { useState, useEffect, useMemo } from 'react';
import Typography from '../../components/Typography/Typography';
import Button from '../../components/Button/Button';
import Pagination from '../../components/Pagination/Pagination';
import DataTable, { dataTableRowClass, dataTableTdClass, dataTableThClass, searchInputClass, TABLE_HEIGHT } from '../../components/DataTable/DataTable';
import ResetWorldIdVerification from '../../dialogs/ResetWorldIdVerification/ResetWorldIdVerification';
import { FiUsers } from 'react-icons/fi';
import { ClipLoader } from 'react-spinners';
import ASSETS from '../../assets';
import toast from 'react-hot-toast';
import { authAPI, MismatchedUser } from '../../services/api';

const itemsPerPage = 10;

const truncateHash = (hash: string) => {
  if (hash.length <= 16) return hash;
  return `${hash.slice(0, 10)}...${hash.slice(-8)}`;
};

const MismatchedUsers = () => {
  const [allUsers, setAllUsers] = useState<MismatchedUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<MismatchedUser | null>(null);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await authAPI.getMismatchedUsers();
      setAllUsers(response.verificationMissMatchesData || []);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to fetch mismatched users';
      setError(errorMessage);
      setAllUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      setCurrentPage(1);
    }, 500);

    setSearchTimeout(timeout);

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [searchTerm]);

  const filteredUsers = useMemo(() => {
    if (!searchTerm.trim()) return allUsers;

    const term = searchTerm.trim().toLowerCase();
    return allUsers.filter((user) => {
      const searchable = [
        user.email,
        user.firstName,
        user.lastName,
        user.oldNullifierHash,
        user.newNullifierHash,
        String(user.id),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchable.includes(term);
    });
  }, [allUsers, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / itemsPerPage));

  const paginatedUsers = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredUsers.slice(start, start + itemsPerPage);
  }, [filteredUsers, currentPage]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handleRefresh = async () => {
    try {
      setIsRefreshing(true);
      setSearchTerm('');
      setCurrentPage(1);
      setError(null);
      await fetchUsers();
      toast.success('Mismatched users refreshed successfully!');
    } catch (err: any) {
      console.error('Error refreshing mismatched users:', err);
      toast.error('Failed to refresh mismatched users. Please try again.');
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleResetClick = (user: MismatchedUser) => {
    setSelectedUser(user);
    setIsResetModalOpen(true);
  };

  const handleResetSuccess = () => {
    setSelectedUser(null);
    fetchUsers();
  };

  const getStatusBadge = (resolved: boolean) => {
    if (resolved) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
          Resolved
        </span>
      );
    }

    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 border border-orange-200">
        Pending
      </span>
    );
  };

  return (
    <div className="flex-grow bg-white rounded-[15px] flex flex-col">
      <div className="rounded-lg">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
          <div className="flex items-center gap-3">
            <Typography variant="h2" className="font-bold title text-xl sm:text-2xl lg:text-2xl">
              Mismatched Users
            </Typography>

            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className={`p-2 rounded-full transition-all duration-200 ${
                isRefreshing
                  ? 'bg-blue-100 cursor-not-allowed'
                  : 'bg-gray-100 hover:bg-blue-100 hover:text-blue-600'
              }`}
              title="Refresh mismatched users list"
            >
              <svg
                className={`w-4 h-4 transition-transform duration-200 ${
                  isRefreshing ? 'animate-spin text-blue-600' : 'text-gray-600'
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </button>
          </div>

          <div className="relative flex-1 sm:flex-none sm:w-80">
            <input
              type="text"
              placeholder="Search by email, name or hash..."
              value={searchTerm}
              onChange={handleSearchChange}
              className={searchInputClass}
            />
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 gap-2">
              {searchTerm && (
                <button
                  onClick={handleClearSearch}
                  className="p-1 hover:bg-gray-100 rounded-full transition-colors duration-200"
                  title="Clear search"
                >
                  <img src={ASSETS.cross} alt="Clear" className="w-3 h-3 text-gray-400 hover:text-gray-600" />
                </button>
              )}
              {ASSETS.search && (
                <img src={ASSETS.search} alt="Search" className="w-4 h-4 text-gray-400" />
              )}
            </div>
          </div>
        </div>

        <DataTable
          footer={
            !loading && !error && filteredUsers.length > 0 ? (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                totalItems={filteredUsers.length}
                itemsPerPage={itemsPerPage}
                embedded
              />
            ) : undefined
          }
        >
            <table className="w-full border-collapse">
              <thead className="sticky top-0 z-10 shadow-[0_1px_0_0_rgba(226,232,240,1)]">
                <tr>
                  <th className={`${dataTableThClass} text-center min-w-[40px]`}>
                    Sr.
                  </th>
                  <th className={`${dataTableThClass} text-left min-w-[200px]`}>
                    Email
                  </th>
                  <th className={`${dataTableThClass} text-left min-w-[120px]`}>
                    First Name
                  </th>
                  <th className={`${dataTableThClass} text-left min-w-[120px]`}>
                    Last Name
                  </th>
                  <th className={`${dataTableThClass} text-left min-w-[180px]`}>
                    Old Nullifier Hash
                  </th>
                  <th className={`${dataTableThClass} text-left min-w-[180px]`}>
                    New Nullifier Hash
                  </th>
                  <th className={`${dataTableThClass} text-center min-w-[100px]`}>
                    Status
                  </th>
                  <th className={`${dataTableThClass} text-center min-w-[140px]`}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} className="text-center align-middle" style={{ height: TABLE_HEIGHT - 48 }}>
                      <div className="flex flex-col items-center justify-center gap-3 h-full">
                        <ClipLoader size={40} color="#3B82F6" />
                      </div>
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan={8} className="text-center align-middle" style={{ height: TABLE_HEIGHT - 48 }}>
                      <div className="flex flex-col items-center justify-center gap-3 h-full">
                        <div className="p-3 bg-red-100 rounded-full">
                          <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                        </div>
                        <Typography variant="h3" className="text-red-500 font-medium">
                          Error loading mismatched users
                        </Typography>
                        <Typography variant="body1" className="text-gray-400">
                          {error}
                        </Typography>
                        <Button
                          label="Retry"
                          variant="outlined"
                          onClick={fetchUsers}
                          className="mt-2"
                        />
                      </div>
                    </td>
                  </tr>
                ) : paginatedUsers.length > 0 ? (
                  paginatedUsers.map((user, index) => (
                    <tr key={user.id} className={dataTableRowClass}>
                      <td className={`${dataTableTdClass} text-center min-w-[40px] font-medium text-slate-500`}>
                        {(currentPage - 1) * itemsPerPage + index + 1}
                      </td>
                      <td className={`${dataTableTdClass} min-w-[200px]`}>
                        {user.email}
                      </td>
                      <td className={`${dataTableTdClass} min-w-[120px]`}>
                        {user.firstName || '-'}
                      </td>
                      <td className={`${dataTableTdClass} min-w-[120px]`}>
                        {user.lastName || '-'}
                      </td>
                      <td
                        className={`${dataTableTdClass} min-w-[180px] font-mono text-xs`}
                        title={user.oldNullifierHash}
                      >
                        {truncateHash(user.oldNullifierHash)}
                      </td>
                      <td
                        className={`${dataTableTdClass} min-w-[180px] font-mono text-xs`}
                        title={user.newNullifierHash}
                      >
                        {truncateHash(user.newNullifierHash)}
                      </td>
                      <td className={`${dataTableTdClass} text-center min-w-[100px]`}>
                        {getStatusBadge(user.resolved)}
                      </td>
                      <td className={`${dataTableTdClass} text-center min-w-[140px]`}>
                        <Button
                          label="Reset Verification"
                          variant="outlined"
                          onClick={() => handleResetClick(user)}
                          disabled={user.resolved}
                          className="!h-[32px] !text-xs !px-3 disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8} className="text-center align-middle" style={{ height: TABLE_HEIGHT - 48 }}>
                      <div className="flex flex-col items-center justify-center gap-3 h-full">
                        <div className="p-3 bg-gray-100 rounded-full">
                          {FiUsers({ className: 'w-8 h-8 text-gray-400' })}
                        </div>
                        <Typography variant="h3" className="text-gray-500 font-medium">
                          {searchTerm ? 'No users found' : 'No mismatched users available'}
                        </Typography>
                        <Typography variant="body1" className="text-gray-400">
                          {searchTerm
                            ? `No users match "${searchTerm}". Try adjusting your search terms.`
                            : 'There are no World ID verification mismatches at this time.'}
                        </Typography>
                        {searchTerm && (
                          <button
                            onClick={handleClearSearch}
                            className="mt-2 px-4 py-2 text-blue-600 hover:text-blue-700 font-medium transition-colors duration-200"
                          >
                            Clear search
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
        </DataTable>
      </div>

      <ResetWorldIdVerification
        open={isResetModalOpen}
        setOpen={setIsResetModalOpen}
        user={selectedUser}
        onSuccess={handleResetSuccess}
      />
    </div>
  );
};

export default MismatchedUsers;
